/**
 * Complete move-animation profile registry.
 *
 * Profiles are authored from the move's declared animation id, contact joint/position, tags and
 * timing. They never change simulation. The procedural rig consumes these immutable descriptors to
 * shape anticipation, contact and recovery while its contact solver targets the authored hitbox.
 */

import { clamp } from '../core/index.js';

const FAMILY_DEFAULTS = Object.freeze({
  straight: Object.freeze({
    arc: 'linear', windup: 0.24, lean: 0.11, twist: 0.18, crouch: 0.02,
    lift: 0, bend: 1, follow: 0.07, shoulderDepth: 0.2, handShape: 'fist',
  }),
  hook: Object.freeze({
    arc: 'round', windup: 0.42, lean: 0.08, twist: 0.48, crouch: 0.04,
    lift: 0, bend: 1, follow: 0.16, shoulderDepth: 0.32, handShape: 'fist',
  }),
  overhead: Object.freeze({
    arc: 'overhead', windup: 0.58, lean: 0.16, twist: 0.16, crouch: 0.12,
    lift: 0, bend: 1, follow: 0.22, shoulderDepth: 0.18, handShape: 'fist',
  }),
  rising: Object.freeze({
    arc: 'rising', windup: 0.48, lean: 0.13, twist: 0.2, crouch: 0.22,
    lift: 0.08, bend: -1, follow: 0.18, shoulderDepth: 0.18, handShape: 'fist',
  }),
  elbow: Object.freeze({
    arc: 'compact', windup: 0.32, lean: 0.18, twist: 0.3, crouch: 0.08,
    lift: 0, bend: 1, follow: 0.08, shoulderDepth: 0.38, handShape: 'fist',
  }),
  palm: Object.freeze({
    arc: 'linear', windup: 0.44, lean: 0.2, twist: 0.24, crouch: 0.1,
    lift: 0, bend: 1, follow: 0.18, shoulderDepth: 0.26, handShape: 'open',
  }),
  kick: Object.freeze({
    arc: 'linear-kick', windup: 0.38, lean: -0.12, twist: 0.24, crouch: 0.08,
    lift: 0.03, bend: 1, follow: 0.12, shoulderDepth: 0.15, handShape: 'guard',
  }),
  'round-kick': Object.freeze({
    arc: 'round-kick', windup: 0.48, lean: -0.16, twist: 0.58, crouch: 0.1,
    lift: 0.05, bend: 1, follow: 0.22, shoulderDepth: 0.24, handShape: 'guard',
  }),
  'low-kick': Object.freeze({
    arc: 'low-kick', windup: 0.3, lean: 0.11, twist: 0.28, crouch: 0.18,
    lift: -0.02, bend: 1, follow: 0.1, shoulderDepth: 0.18, handShape: 'guard',
  }),
  stomp: Object.freeze({
    arc: 'stomp', windup: 0.5, lean: 0.22, twist: 0.06, crouch: 0.05,
    lift: 0.12, bend: 1, follow: 0.2, shoulderDepth: 0.08, handShape: 'guard',
  }),
  knee: Object.freeze({
    arc: 'knee', windup: 0.4, lean: 0.2, twist: 0.2, crouch: 0.15,
    lift: 0.06, bend: 1, follow: 0.12, shoulderDepth: 0.32, handShape: 'guard',
  }),
  body: Object.freeze({
    arc: 'body', windup: 0.48, lean: 0.36, twist: 0.08, crouch: 0.16,
    lift: 0, bend: 1, follow: 0.22, shoulderDepth: 0.08, handShape: 'guard',
  }),
  stance: Object.freeze({
    arc: 'stance', windup: 0.25, lean: 0.04, twist: 0.12, crouch: 0.12,
    lift: 0, bend: 1, follow: 0, shoulderDepth: 0.2, handShape: 'open',
  }),
  parry: Object.freeze({
    arc: 'parry', windup: 0.16, lean: -0.04, twist: 0.18, crouch: 0.08,
    lift: 0, bend: 1, follow: 0, shoulderDepth: 0.44, handShape: 'open',
  }),
  grapple: Object.freeze({
    arc: 'grapple', windup: 0.4, lean: 0.18, twist: 0.12, crouch: 0.1,
    lift: 0, bend: 1, follow: 0.12, shoulderDepth: 0.62, handShape: 'grip',
  }),
  finisher: Object.freeze({
    arc: 'finisher', windup: 0.72, lean: 0.28, twist: 0.7, crouch: 0.2,
    lift: 0.1, bend: -1, follow: 0.35, shoulderDepth: 0.4, handShape: 'fist',
  }),
});

/** Signature motion accents. Every other move still receives a complete data-authored profile. */
const SIGNATURE = Object.freeze({
  'ky.crescent': Object.freeze({ twist: 0.92, follow: 0.3, shoulderDepth: 0.36 }),
  'ky.heeldrop': Object.freeze({ family: 'stomp', lift: 0.2, crouch: 0.18 }),
  'ky.veinsplit': Object.freeze({ lean: 0.3, twist: 0.36, follow: 0.25 }),
  'ky.readcut': Object.freeze({ family: 'rising', crouch: 0.34, lift: 0.14 }),
  'ky.flowpivot': Object.freeze({ family: 'round-kick', twist: 1.05, follow: 0.32 }),
  'ky.severance': Object.freeze({
    twist: 1.12, lean: 0.36, follow: 0.44, recoveryRecoil: 0.38, recoveryGuard: 0.48,
  }),
  'bw.hammer': Object.freeze({
    family: 'overhead', windup: 0.82, lean: 0.3, recoveryDip: 0.105, recoveryRecoil: 0.3,
  }),
  'bw.heelturn': Object.freeze({ family: 'round-kick', twist: 0.86, follow: 0.34 }),
  'bw.uppercut': Object.freeze({ family: 'rising', crouch: 0.3, lift: 0.12 }),
  'bw.shoulder': Object.freeze({ family: 'body', lean: 0.52, crouch: 0.24 }),
  'bw.gritslam': Object.freeze({ family: 'rising', windup: 0.72, lift: 0.18, follow: 0.34 }),
  'bw.anchorcharge': Object.freeze({ family: 'body', lean: 0.58, crouch: 0.28 }),
  'bw.anchorhammer': Object.freeze({ family: 'overhead', windup: 0.88, follow: 0.38 }),
  'bw.tectonic': Object.freeze({
    twist: 0.82, lean: 0.46, follow: 0.5, recoveryDip: 0.12, recoveryRecoil: 0.34,
  }),
});

function contains(move, ...tokens) {
  const text = `${move.anim} ${move.id} ${move.tags.join(' ')}`;
  return tokens.some((token) => text.includes(token));
}

function familyFor(move, joint) {
  if (move.category === 'finisher') return 'finisher';
  if (move.isThrow) return 'grapple';
  if (move.category === 'stance') return 'stance';
  if (contains(move, 'parry')) return 'parry';
  if (joint === 'torso') return 'body';
  if (joint.startsWith('knee')) return 'knee';
  if (joint.startsWith('foot')) {
    if (contains(move, 'stomp', 'heeldrop', 'groundrake')) return 'stomp';
    if (contains(move, 'crescent', 'heelturn', 'sweep', 'pivot', 'juggleender')) {
      return 'round-kick';
    }
    if (move.level === 'low' || contains(move, 'low', 'shin', 'scrape')) return 'low-kick';
    return 'kick';
  }
  if (joint.startsWith('elbow')) return 'elbow';
  if (contains(move, 'hammer', 'slam')) return 'overhead';
  if (contains(move, 'upper', 'riser', 'lift', 'hook')) {
    return contains(move, 'hook') ? 'hook' : 'rising';
  }
  if (contains(move, 'backfist', 'backhand', 'turn', 'pivot')) return 'hook';
  if (contains(move, 'palm')) return 'palm';
  return 'straight';
}

function profileFor(move) {
  const hitbox = move.hitboxes[0] || null;
  const joint = hitbox ? hitbox.joint : 'throw';
  const signature = SIGNATURE[move.anim] || null;
  const derivedFamily = familyFor(move, joint);
  const family = signature && signature.family ? signature.family : derivedFamily;
  const defaults = FAMILY_DEFAULTS[family];
  const side = joint.endsWith('L') ? 'left' : joint.endsWith('R') ? 'right' : 'center';
  const power = clamp(move.damage / (move.category === 'finisher' ? 112 : 44), 0, 1);
  const commitment = move.tags.includes('commitment') ? 1 : 0;
  const range = clamp((move.aiHints.range || 0.8) / 1.5, 0.2, 1.2);
  const agile = move.fighterPrefix === 'ky';
  const contactX = hitbox ? Math.max(hitbox.ox, hitbox.ex) : Math.min(0.62, 0.36 + range * 0.24);
  const contactY = hitbox ? (hitbox.oy + hitbox.ey) * 0.5 : 1.24;
  const profile = {
    id: move.anim,
    moveId: move.id,
    family,
    arc: defaults.arc,
    joint,
    side,
    plant: side === 'left' ? 'right' : side === 'right' ? 'left' : 'both',
    contactX,
    contactY,
    power,
    range,
    windup: clamp(defaults.windup + commitment * 0.12 + power * 0.08, 0.12, 0.96),
    lean: defaults.lean + power * 0.08,
    twist: defaults.twist + (move.tracking > 0.8 ? 0.1 : 0),
    crouch: defaults.crouch,
    lift: defaults.lift,
    bend: defaults.bend,
    follow: defaults.follow + power * 0.05,
    shoulderDepth: defaults.shoulderDepth,
    handShape: defaults.handShape,
    recoveryStyle: agile ? 'snap-guard' : 'mass-settle',
    recoveryRecoil: clamp(defaults.follow * (agile ? 0.82 : 0.56) + power * 0.08, 0.04, 0.42),
    recoveryTwist: clamp(defaults.twist * (agile ? 0.34 : 0.22), 0.03, 0.38),
    recoveryDip: clamp(defaults.crouch * (agile ? 0.14 : 0.42) + power * (agile ? 0.01 : 0.035),
      0.004, 0.13),
    recoveryGuard: agile ? clamp(0.34 + range * 0.16, 0.36, 0.54)
      : clamp(0.12 + power * 0.12, 0.12, 0.25),
    source: signature ? 'signature-override' : `authored-${family}`,
  };
  if (signature) {
    for (const key of Object.keys(signature)) {
      if (key !== 'family') profile[key] = signature[key];
    }
  }
  return Object.freeze(profile);
}

export class AnimationRegistry {
  constructor(moves) {
    this.profiles = new Map();
    this.moveCount = moves.length;
    for (const move of moves) {
      if (this.profiles.has(move.anim)) {
        throw new Error(`Animation id "${move.anim}" is shared; profiles must be unambiguous.`);
      }
      this.profiles.set(move.anim, profileFor(move));
    }
  }

  get(id) {
    const profile = this.profiles.get(id);
    if (!profile) throw new Error(`Missing animation profile "${id}".`);
    return profile;
  }

  has(id) {
    return this.profiles.has(id);
  }

  coverage(moves) {
    const missing = [];
    const families = Object.create(null);
    let signatureOverrides = 0;
    for (const move of moves) {
      const profile = this.profiles.get(move.anim);
      if (!profile) {
        missing.push({ moveId: move.id, anim: move.anim });
        continue;
      }
      families[profile.family] = (families[profile.family] || 0) + 1;
      if (profile.source === 'signature-override') signatureOverrides++;
    }
    return {
      moves: moves.length,
      profiles: this.profiles.size,
      missing,
      families,
      signatureOverrides,
      complete: missing.length === 0 && this.profiles.size === moves.length,
    };
  }
}
